import { Component, OnInit } from '@angular/core';
import { WealthOnboardingService } from 'src/app/services/wealth-onboarding.service';
import { OnboardingClient } from 'src/app/shared/models/onboarding/onboarding-client';
import { ClientListResponse } from 'src/app/shared/models/onboarding/client-list-response';
import { SettingsService } from 'src/app/services/settings.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Note } from 'src/app/shared/models/note/note';
import { OnboardingClientStep } from 'src/app/shared/models/onboarding/onboarding-client-step';
import { OnboardingClientCategory } from 'src/app/shared/models/onboarding/onboarding-client-category';
import { UserService } from 'src/app/services/user.service';
import { UserFullDetails } from 'src/app/shared/models/users/user-full-details';

@Component({
  selector: 'app-wealth-onboarding',
  templateUrl: './wealth-onboarding.component.html',
  styleUrls: ['./wealth-onboarding.component.scss'],
})
export class WealthOnboardingComponent implements OnInit {
  isCollapsed = false;
  showError: boolean = false;
  errorResponse: HttpErrorResponse;

  loadingClientList = false;
  infoLoading: boolean = false;

  filterableClients: OnboardingClient[] = [];
  clients: OnboardingClient[] = [];
  selectedUserFullDetails: UserFullDetails;
  showUserProfileModal: boolean = false;
  loadingProfile: boolean = false;

  clientListResponse: ClientListResponse = new ClientListResponse();
  searchText: string = '';
  selectedClientId: number | null = null;
  get createdByUserId(): number {
    return this.settingsService.user?.id || 0;
  }
  get createdByMeUserName(): string {
    return this.settingsService.userProfile.fullName ?? 'Me';
  }
  
  onboardingClient: OnboardingClient;
  selectedClientState: string = 'unverified';
  clientStateOptions = [
    { label: 'Unverified', value: 'unverified' },
    { label: 'Verified', value: 'verified' }
  ];
  
  newNote: Note;

  constructor(
    private wealthService: WealthOnboardingService,
    private userService: UserService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    this.settingsService.ValidateGlobalVars().then(() => {
      this.loadClients();
    });
  }

  loadClients() {
    this.loadingClientList = true;
    const onlyOnboardedCompleted = this.selectedClientState === 'verified';
    this.wealthService.GetWealthClients(onlyOnboardedCompleted).subscribe({
      next: (response) => {
        this.clientListResponse = response;
        this.clients = response.clients;
        this.filterableClients = [...this.clients];
      },
      error: (error) => this.popupError(error),
      complete: () => (this.loadingClientList = false),
    });
  }

  onStateFilterChange() {
    this.selectedClientId = null;
    this.loadClients();
  }

  getClientStateLabel(): string {
    return this.clientStateOptions.find(o => o.value === this.selectedClientState)?.label ?? '';
  }

  onGridCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.selectedClientId = null;
  }

  onClientSelect(clientId: number) {
    if (this.selectedClientId === clientId) return;

    this.isCollapsed = true;
    this.infoLoading = true;
    this.selectedClientId = clientId;

    this.wealthService.GetWealthClientInformation(clientId).subscribe({
      next: (response) => {
        this.onboardingClient = response;
      },
      error: (error) => {
        this.popupError(error);
        this.selectedClientId = null;
      },
      complete: () => (this.infoLoading = false),
    });
  }

  toggleStepUpdate(step: OnboardingClientStep) {
    step.isUpdating = true;
    step.error = null;

    if (step.emailTemplateId) {
      step.isCompleted = true;
    }

    this.wealthService.UpdateStep(this.onboardingClient.userId, step.id, step.isCompleted, this.createdByUserId).subscribe({
      next: () => {
        this.addStepNote(step);
      },
      error: (error) => {
        step.error = error;
        step.isCompleted = !step.isCompleted;
        this.popupError(error);
      },
      complete: () => {
        step.isUpdating = false;
      },
    });
  }

  beginArbitrageProcess() {
    if (!this.selectedClientId) return;
    this.wealthService.BeginArbitrageOnboarding(this.selectedClientId, this.createdByUserId).subscribe({
      next: (response) => {
        this.handleNoteCreated(this.convertToNote(response));
        this.isCollapsed = false;
        this.selectedClientId = null;
        this.loadClients();
      },
      error: (error) => this.popupError(error)
    });
  }

  completeWealthProcess() {
    if (!this.selectedClientId) return;
    this.wealthService.CompleteOnboarding(this.selectedClientId, this.createdByUserId).subscribe({
      next: (response) => {
        this.handleNoteCreated(this.convertToNote(response));
        this.isCollapsed = false;
        this.selectedClientId = null;
        this.loadClients();
      },
      error: (error) => this.popupError(error)
    });
  }

  restartVerification() {
    if (!this.selectedClientId || !confirm('Are you sure you want to restart verification for this client? All progress will be lost.')) return;
    this.wealthService.RestartVerification(this.selectedClientId, this.createdByUserId).subscribe({
      next: (response) => {
        this.handleNoteCreated(this.convertToNote(response));
        this.isCollapsed = false;
        this.selectedClientId = null;
        this.loadClients();
      },
      error: (error) => this.popupError(error)
    });
  }

  canComplete(): boolean {
    if (!this.onboardingClient) return false;
    return this.onboardingClient.totalStepsCount > 0 && this.onboardingClient.completedStepsCount === this.onboardingClient.totalStepsCount && !this.onboardingClient.isOnboardingCompleted;
  }

  popupError(error: HttpErrorResponse) {
    this.errorResponse = { ...error };
    this.showError = true;
  }

  parseAndDisplayError(error: HttpErrorResponse) {
    this.popupError(error);
  }

  handleNoteCreated(note: Note) {
    this.newNote = note;
  }

  onEditProfileClick() {
    if (this.selectedClientId == null) return;
    this.loadingProfile = true;
    this.userService.GetUserDetails(this.selectedClientId).subscribe({
      next: (response) => {
        this.selectedUserFullDetails = response;
        this.showUserProfileModal = true;
      },
      error: (error) => this.popupError(error),
      complete: () => (this.loadingProfile = false),
    });
  }

  handleUserProfileUpdated(userFullDetails: UserFullDetails) {
    if (userFullDetails.user.active == false) {
      this.clients = this.clients.filter((c) => c.userId !== userFullDetails.user.id);
      this.filterableClients = this.filterableClients.filter((c) => c.userId !== userFullDetails.user.id);
      this.isCollapsed = false;
      this.selectedClientId = null;
    }
  }

  private addStepNote(step: OnboardingClientStep) {
    const notePrefix = step.isCompleted ? 'Completed:' : 'Reverted:';
    let addedNote: Note = new Note();
    addedNote.id = 0;
    addedNote.clientId = this.onboardingClient.userId;
    addedNote.note = `${notePrefix} ${step.description}`;
    addedNote.dateTimeCreated = new Date();
    addedNote.createdByUserId = this.createdByUserId;
    addedNote.createdByUserFullName = this.createdByMeUserName;
    this.newNote = addedNote;
  }

  private convertToNote(response: any): Note {
    const note = new Note();
    note.id = response.id;
    note.note = response.note;
    note.dateTimeCreated = response.dateTimeCreated;
    note.createdByUserId = response.createdByUserId;
    return note;
  }

  onCategoryClick(category: OnboardingClientCategory) {
    if (category.showSteps == null) {
      category.showSteps = !this.stepIsVisible(category);
    } else {
      category.showSteps = !category.showSteps;
    }
  }

  stepIsVisible(category: OnboardingClientCategory): boolean {
    if (category.showSteps == null) {
      const isFirst = this.onboardingClient.onboardingCategoriesAndSteps[0]?.id === category.id;
      
      if (isFirst && !category.isCompleted) {
        return true;
      }

      if (category.hasStarted && !category.isCompleted) {
        return true;
      }

      return false;
    }

    return category.showSteps;
  }
}
